/**
 * A store that manages active frame size transitions on Open Canvas frames.
 *
 * Size transitions are used in the Designer to handle high-frequency updates to frame sizes, before
 * committing the final size to the global Flux store.
 *
 * This store is treated as an external store and allows reading and updating values using dedicated hooks.
 * @see useFrameSizeTransition
 * @see useFrameSizeTransitionControl
 */
class FrameSizeTransitionStore {
  private subscribers = new Map<string, Set<() => void>>();
  private globalSubscribers = new Set<() => void>();

  private activeTransitions: Map<string, FrameSizeTransition> = new Map();

  private activeTransitionInheritingFrames: Map<string, string[]> = new Map();

  subscribe(frameId: string, callback: () => void) {
    let set = this.subscribers.get(frameId);
    if (!set) {
      set = new Set();
      this.subscribers.set(frameId, set);
    }

    set.add(callback);

    return () => {
      const subs = this.subscribers.get(frameId);

      if (subs) {
        subs.delete(callback);

        if (subs.size === 0) {
          this.subscribers.delete(frameId);
        }
      }
    };
  }

  subscribeGlobal(callback: () => void) {
    this.globalSubscribers.add(callback);
    return () => this.globalSubscribers.delete(callback);
  }

  getSnapshot(frameId: string): FrameSizeTransition | null {
    return this.activeTransitions.get(frameId) || null;
  }

  startTransition(
    frameId: string,
    initialValue: FrameSizeTransition,
    inheritingFrameIds: string[]
  ) {
    const frameIds = [frameId, ...inheritingFrameIds];

    frameIds.forEach((id) => {
      if (this.activeTransitions.has(id)) {
        throw new Error(
          `[FrameTransitionStore] Active transition already exists for frame ${id}`
        );
      }

      this.activeTransitions.set(id, initialValue);
    });

    this.activeTransitionInheritingFrames.set(frameId, inheritingFrameIds);
    this.notifySubscribers(frameIds);
  }

  updateTransitionValue(frameId: string, value: FrameSizeTransition) {
    const inheritingFrameIds =
      this.activeTransitionInheritingFrames.get(frameId) || [];
    const frameIds = [frameId, ...inheritingFrameIds];

    frameIds.forEach((id) => {
      const currentTransition = this.activeTransitions.get(id);
      if (!currentTransition) {
        throw new Error(
          `[FrameTransitionStore] No active transition for frame ${id} when trying to update value`
        );
      }

      this.activeTransitions.set(id, value);
    });

    this.notifySubscribers(frameIds);
  }

  endTransition(frameId: string) {
    const inheritingFrameIds =
      this.activeTransitionInheritingFrames.get(frameId) || [];
    const frameIds = [frameId, ...inheritingFrameIds];

    frameIds.forEach((id) => {
      const activeTransition = this.activeTransitions.get(id);
      if (!activeTransition) return;

      this.activeTransitions.delete(id);
    });

    this.activeTransitionInheritingFrames.delete(frameId);
    this.notifySubscribers(frameIds);
  }

  private notifySubscribers = (frameIds: string[]) => {
    const subsArray = frameIds.map((frameId) => this.subscribers.get(frameId));
    for (const subs of subsArray) {
      if (subs) {
        subs.forEach((cb) => cb());
      }
    }
    this.notifyGlobalSubscribers();
  };

  private notifyGlobalSubscribers() {
    this.globalSubscribers.forEach((cb) => cb());
  }
}

export const forTestSuite = {
  FrameSizeTransitionStore,
};

/**
 * A frame size transition represents a set of frame size values that are in the process of being updated.
 */
export type FrameSizeTransition = {
  /**
   * The width of the frame as it is being transitioned. Use `undefined` to indicate that the width should be unchanged.
   */
  width?: number;
  /**
   * The height of the frame as it is being transitioned. Use `undefined` to indicate that the height should be unchanged.
   */
  height?: number;
};

export const frameSizeTransitionStore = new FrameSizeTransitionStore();
